import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Converte "#2563EB" em "37 99 235", formato aceito por rgb(var(--x) / alpha). */
export function hexToRgbChannels(hex: string): string {
  const clean = hex.replace('#', '').trim();
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((char) => char + char)
          .join('')
      : clean;

  const value = Number.parseInt(full.slice(0, 6), 16);
  if (Number.isNaN(value)) return '37 99 235';

  return `${(value >> 16) & 255} ${(value >> 8) & 255} ${value & 255}`;
}

/** Escolhe texto claro ou escuro conforme a luminancia do fundo. */
export function readableTextColor(hex: string): '#0F172A' | '#FFFFFF' {
  const [r, g, b] = hexToRgbChannels(hex).split(' ').map(Number);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#0F172A' : '#FFFFFF';
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

export function formatBytes(value: string | number | null | undefined, decimals = 1): string {
  const bytes = typeof value === 'string' ? Number(value) : (value ?? 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';

  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), BYTE_UNITS.length - 1);
  const size = bytes / 1024 ** index;

  return `${size.toFixed(index === 0 ? 0 : decimals).replace('.', ',')} ${BYTE_UNITS[index]}`;
}

/** 95000 -> "1min 35s"; usado nas durações de playlist. */
export function formatDuration(ms: number | null | undefined): string {
  if (!ms || ms <= 0) return '—';

  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}min`;
  if (minutes > 0) return `${minutes}min ${String(seconds).padStart(2, '0')}s`;
  return `${seconds}s`;
}

/** Diferença legível para "última comunicação". */
export function formatRelative(date: string | Date | null | undefined): string {
  if (!date) return 'nunca';

  const target = typeof date === 'string' ? new Date(date) : date;
  const seconds = Math.floor((Date.now() - target.getTime()) / 1000);

  if (seconds < 10) return 'agora';
  if (seconds < 60) return `há ${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `há ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours}h`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `há ${days} dia${days > 1 ? 's' : ''}`;

  const months = Math.floor(days / 30);
  if (months < 12) return `há ${months} ${months > 1 ? 'meses' : 'mês'}`;

  return `há ${Math.floor(months / 12)} ano(s)`;
}

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? '—' : dateFormatter.format(date);
}

/**
 * Data pura -- coluna `date`, sem hora -- formatada sem conversao de fuso.
 *
 * `formatDate` serve a instantes: ele converte para o fuso de quem olha, que e
 * o certo para "gerado às 14h32". Para uma data sem hora isso é um erro, e um
 * erro silencioso: o prazo de um encarte chega do servidor como
 * `2026-10-15T00:00:00.000Z` e, convertido para o horário de Brasília, vira
 * 14/10 às 21h. A tela mostrava o dia anterior ao que está gravado -- e quem
 * marcou o encarte para quinta via quarta na lista.
 *
 * Aqui não há conversão nenhuma: o ano, o mês e o dia são lidos como estão e
 * escritos como estão, porque é exatamente isso que uma data sem hora é.
 */
export function formatDateOnly(value: string | Date | null | undefined): string {
  if (!value) return '—';

  const texto = typeof value === 'string' ? value : value.toISOString();
  const [ano, mes, dia] = texto.slice(0, 10).split('-');

  return ano && mes && dia ? `${dia}/${mes}/${ano}` : '—';
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? '—' : dateTimeFormatter.format(date);
}

export function formatNumber(value: number | null | undefined): string {
  return new Intl.NumberFormat('pt-BR').format(value ?? 0);
}

/** Formata um valor em centavos como moeda brasileira. */
export function formatCurrency(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
}

/** Formata CNPJ/CPF a partir dos dígitos. */
export function formatDocument(value: string | null | undefined): string {
  if (!value) return '—';
  const digits = value.replace(/\D/g, '');

  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value;
}

export function formatPhone(value: string | null | undefined): string {
  if (!value) return '—';
  const digits = value.replace(/\D/g, '');

  if (digits.length === 11) return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  if (digits.length === 10) return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  return value;
}

export const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export function formatWeekdays(days: number[] | null | undefined): string {
  if (!days || days.length === 0 || days.length === 7) return 'Todos os dias';

  const sorted = [...days].sort((a, b) => a - b);
  const isWeekdaysOnly = sorted.join(',') === '1,2,3,4,5';
  if (isWeekdaysOnly) return 'Segunda a sexta';

  const isWeekend = sorted.join(',') === '0,6';
  if (isWeekend) return 'Fins de semana';

  return sorted.map((day) => WEEKDAY_LABELS[day]).join(', ');
}

/** Inicais para avatares. */
export function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
